import { PROGRAM_TYPE, REACT_QUERY_KEYS } from '@intigral/constant';
import { Button, FontIcon, RentTimer, Text } from '@intigral/shared';
import styles from '@intigral/shared/styles/components/carousel.module.sass';
import { MovieCard } from '@intigral/ui';
import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ButtonTypes, PROVIDER, COMPONENT_TYPES } from '../../config/constants';
import { useDispatch } from 'react-redux';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import { trackCarouselItemDetailClick } from '../../helpers/trackCarouselItemDetailClick';
import { Trailer } from '../../features/detail-overlay/components/elements';
import playerService from '../../helpers/bitmovinPlayer';
import h, { getLearnActionConfig } from '../../helpers/common';
import f2 from '../../helpers/formatter2';
import { isSubscriptionLockedContent, showUpgradeOrOpenDetail } from '../../helpers/vod';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import useDetailOverlay from '../../hooks/useDetailOverlay';
import usePPVManagement from '../../hooks/usePPVManagement';
import { useReminderItem } from '../../hooks/useReminderItem';
import useTimeToExpire from '../../hooks/useTimeToExpire';
import { useTvod } from '../../hooks/useTvod';
import { useUserReminders } from '../../store/reducers/reminderSlice';
import { __ } from '../../translations';
import { getUserInfo } from '../../utils/user';
import { isGuestUser, showGuestPromotionPopup } from '../../config/guestMode';
import AddReminderButton from './AddReminderButton';
import AddWatchlistButton from './AddWatchlistButton';
import LikeDislikeButton from './LikeDislikeButton';
import { MoviePlayButton } from './MoviePlayButton';

/** Delay before starting the hover trailer so quick mouse-overs do not mount Bitmovin. */
const TRAILER_HOVER_DELAY_MS = 500;

interface VodeItemProps {
    // REMOVED: key is a special React prop and cannot be accessed
    // key: any;
    index: number;
    item: any;
    meta: any;
    componentType: string;
    componentName: string;
    type: string;
    onItemClick?: (item: any, index: number) => void;
}

const getTvodPriceProps = (priceWithVat, currency, isRented) => {
    if (isRented) {
        return undefined;
        //return { free: true, freePriceOrText: __('tvod_poster_rented') };
    }
    const isSar = currency?.toUpperCase() === 'SAR';
    return {
        price: priceWithVat,
        currency: currency, //isSar ? undefined : currency,
        shouldShowSarSymbol: false, //isSar,
    };
    // return `${priceWithVat} ${currency}`;
};

const VodItemLandscape = ({ item, meta, componentType, componentName, index, type, onItemClick = undefined }) => {
    const queryClient = useQueryClient();
    const queryKeyItemId = item?.programType === PROGRAM_TYPE.MOVIE ? item?.Id : item?.guid;
    queryClient.setQueryData([REACT_QUERY_KEYS.VOD_DETAILS, queryKeyItemId], (old) => {
        if (old === undefined || old === null) return item;
        return undefined;
    });
    const { priceInfo, isTvod, expiryDate, isRented } = useTvod(item);
    const { priceWithVat = '', currency = '' } = priceInfo || {};
    const openDetailOverlay = useDetailOverlay();
    const dispatch = useDispatch();
    const { shouldShowLock } = usePPVManagement();
    const { promotionProviderImages, autoTrailerCarousel } = useConfigurationSections();
    const autoTrailerCarouselConfiguration = autoTrailerCarousel;

    const userInfo = getUserInfo();
    const { data: reminderData } = useUserReminders({ detailed: false }, userInfo);
    const [isMuted, setIsMuted] = useState(true);
    const [isActive, setIsActive] = useState(false);
    const trailerHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTrailerHoverTimer = useCallback(() => {
        if (trailerHoverTimerRef.current) {
            clearTimeout(trailerHoverTimerRef.current);
            trailerHoverTimerRef.current = null;
        }
    }, []);

    const handleSetIsActive = useCallback(
        (active: boolean) => {
            clearTrailerHoverTimer();
            if (active) {
                trailerHoverTimerRef.current = setTimeout(() => {
                    trailerHoverTimerRef.current = null;
                    setIsActive(true);
                }, TRAILER_HOVER_DELAY_MS);
                return;
            }
            setIsActive(false);
        },
        [clearTrailerHoverTimer]
    );

    useEffect(() => () => clearTrailerHoverTimer(), [clearTrailerHoverTimer]);

    let tvodRentNowBtnText = __('tvod_rent_now_button_details') || '';
    if (priceWithVat) {
        tvodRentNowBtnText = tvodRentNowBtnText.replace('{priceWithVat}', `<label  dir="ltr"> ${priceWithVat}`);
    } else {
        tvodRentNowBtnText = tvodRentNowBtnText.replace(' - {priceWithVat}', ``);
    }
    tvodRentNowBtnText = tvodRentNowBtnText.replace('{currency}', `${currency}</label>`);
    tvodRentNowBtnText = tvodRentNowBtnText.replace(/<label(.*?)>/g, '<span$1>').replace(/<\/label>/g, '</span>');

    const { durationInMinutes, hours, minutes } = useTimeToExpire(expiryDate);

    const { track = () => undefined } = useContext(MixpanelContext) || {};

    const trackCarouselItemClick = () => {
        trackCarouselItemDetailClick({ track, item, componentType, componentName, meta });
    };

    const openVodDetailOverlay = () => {
        if (item?.useCaseId) {
            playerService.setUseCaseId(item.useCaseId);
        }
        openDetailOverlay(item);
    };

    const handleCardClick = (event) => {
        if (componentType === COMPONENT_TYPES.SEARCH_RESULTS && showGuestPromotionPopup(event)) {
            return;
        }

        trackCarouselItemClick();
        if (typeof onItemClick === 'function') onItemClick(item, index);
        // Direct card click always opens detail overlay (even when locked).
        // Locked play button in the hover popup stops propagation and shows upgrade modal instead.
        showUpgradeOrOpenDetail({ item, locked, dispatch, openDetail: openVodDetailOverlay });
    };

    const handleMute = () => {
        playerService.toggleMute();
        setIsMuted(!isMuted);
    };

    const { isLocked } = shouldShowLock(item) || {};

    const locked = (!isTvod && isLocked) || item?.isLocked;
    const isSubscriptionLocked = isSubscriptionLockedContent(item, locked);

    const { isComingSoonItem, isReminded } = useReminderItem(item, reminderData ?? []);
    const isGuest = isGuestUser(userInfo);
    const showLikeDislike = !isGuest && !!getLearnActionConfig()?.isLikeDislikeEnable;

    // season rails: play resolves an episode
    const isSeasonCard = type === 'SEASONS' || type === 'SEASONSPROMO' || type === 'SEASONEPISODES';

    return (
        <MovieCard
            heading={
                <Text as="h2" variant="h2" size={{ default: '24', md: '28' }} alignment="start" margin="0">
                    {item?.text}
                </Text>
            }
            onClick={handleCardClick}
            reminderButton={isComingSoonItem && <AddReminderButton item={item} category="VOD" isComingSoon iconOnly />}
            play={
                <MoviePlayButton
                    isTvod={isTvod}
                    isRented={isRented}
                    item={item}
                    componentName={componentName}
                    componentType={componentType}
                    meta={meta}
                    tvodRentText={tvodRentNowBtnText}
                    openDetailOverlay={openDetailOverlay}
                    hours={hours}
                    minutes={minutes}
                    locked={locked}
                    isComingSoonItem={isComingSoonItem}
                    seasonId={isSeasonCard ? item?.Id : undefined}
                />
            }
            price={isTvod && priceWithVat && getTvodPriceProps(priceWithVat, currency, isRented)}
            timer={
                isTvod &&
                isRented && (
                    <RentTimer
                        timeCount={hours > 0 ? [hours, 'hour'] : [minutes, 'minute']}
                        timeType={
                            hours > 0
                                ? __('tvod_rent_expiration_hours')
                                : minutes > 0
                                ? __('tvod_rent_expiration_minutes')
                                : ''
                        }
                        labelRemaining={__('tvod_rent_remaining_time')}
                        {...(hours < 0 && minutes < 0 ? { expired: true } : {})}
                        expiredLabel={__('tvod_rent_expired')}
                        {...(Number(durationInMinutes) < Number(h.getTVodConfig()?.diffExpirationTimeInMinutes)
                            ? { almostEnded: true }
                            : {})}
                    />
                )
            }
            watchlistButton={
                <AddWatchlistButton
                    item={item}
                    type={ButtonTypes.VodeItem}
                    componentType={componentType}
                    componentName={componentName}
                />
            }
            endActions={
                showLikeDislike ? (
                    <LikeDislikeButton
                        itemGuid={item?.guid || ''}
                        item={item}
                        likeLabel=""
                        dislikeLabel=""
                        square
                    />
                ) : (
                    <></>
                )
            }
            provider={[
                h.getImagizerUrl(
                    promotionProviderImages?.[item?.provider]?.imageUrl,
                    PROVIDER.WIDTH,
                    PROVIDER.HEIGHT,
                    PROVIDER.QUALITY
                ),
            ]}
            cover={
                h.getImagizerUrl(
                    item?.image,
                    Number(autoTrailerCarouselConfiguration?.portait_image_width) || 300,
                    Number(autoTrailerCarouselConfiguration?.portait_image_height) || 400,
                    Number(autoTrailerCarouselConfiguration?.portait_image_quality) || 100
                ) || null
            }
            rating={item?.ratings ? __(`rating_${item?.ratings.toLowerCase()}`) : null}
            info={[
                ...(item?.genre && Array.isArray(item?.genre) ? item?.genre.map((g) => __(g)) || [] : []),
                item?.duration > 0 ? `${f2.convertSecondsToMinutes(item?.duration)} ${__('min')}` : null,
                item?.year ? `${item?.year}` : null,
            ]}
            page={{
                onClick: (e) => {
                    e.stopPropagation();
                    openVodDetailOverlay();
                },
            }}
            picture={
                isActive && item?.trailerMediaUrl && autoTrailerCarouselConfiguration?.is_enabled ? (
                    <Trailer source={item?.trailerMediaUrl} isMuted={isMuted} item={item}>
                        <Button
                            primary
                            data-test-id="mute-button"
                            className={styles['button-mute']}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleMute();
                            }}
                        >
                            <FontIcon
                                name={isMuted ? 'volume-off' : 'volume-high'}
                                size="smaller"
                                md="smaller"
                            ></FontIcon>
                        </Button>
                    </Trailer>
                ) : (
                    <img
                        src={
                            h.getImagizerUrl(
                                item?.image_landscape,
                                Number(autoTrailerCarouselConfiguration?.landscape_image_width) || 750,
                                Number(autoTrailerCarouselConfiguration?.landscape_image_height) || 400
                            ) || null
                        }
                        alt=""
                    />
                )
            }
            setIsActive={handleSetIsActive}
            {...(locked ? { icon: 'locked' } : {})}
            {...(isComingSoonItem && isReminded ? { icon: 'remind' } : {})}
            guid={item?.guid}
            rented={isRented}
        />
    );
};

export default ({
    // REMOVED: key is a special React prop and cannot be destructured
    index,
    item,
    meta,
    componentType,
    componentName,
    type,
    onItemClick = undefined,
}: VodeItemProps) => {
    return (
        <VodItemLandscape
            // REMOVED: Don't pass key prop explicitly - React handles it automatically
            index={index}
            item={item}
            meta={meta}
            componentType={componentType}
            componentName={componentName}
            onItemClick={onItemClick}
            type={type}
        />
    );
};
